/**
 * Tier Service - Frontend
 * 
 * Core tier management logic for the frontend.
 * Handles tier configuration loading, mapping, and feature/content access checks.
 */

import tierConfig from '../config/tiers.json';

// Cache the tier configuration
let tierConfiguration = null;

/**
 * Load and validate tier configuration
 * @returns {Object} Tier configuration object
 */
export function loadTierConfig() {
  if (tierConfiguration) {
    return tierConfiguration;
  }
  
  tierConfiguration = tierConfig;
  return tierConfiguration;
}

/**
 * Get tier by ID
 * @param {string} tierId - Tier ID (free, basic, pro, elite)
 * @returns {Object|null} Tier configuration or null if not found
 */
export function getTier(tierId) {
  const config = loadTierConfig();
  return config.tiers[tierId] || null;
}

/**
 * Get all tiers
 * @returns {Object} All tier configurations
 */
export function getAllTiers() {
  const config = loadTierConfig();
  return config.tiers;
}

/**
 * Get tier hierarchy array
 * @returns {Array<string>} Tier hierarchy array
 */
export function getTierHierarchy() {
  const config = loadTierConfig();
  return config.tier_hierarchy || [];
}

/**
 * Map Stripe priceId to tier
 * @param {string} priceId - Stripe price ID
 * @returns {Object|null} Tier configuration or null if not found
 */
export function getTierFromPriceId(priceId) {
  if (!priceId) return null;
  
  const tiers = getAllTiers();
  
  // Check all tiers for matching priceId (monthly or yearly)
  for (const tierId in tiers) {
    const tier = tiers[tierId];
    const monthlyPriceId = tier.stripe?.priceId?.monthly;
    const yearlyPriceId = tier.stripe?.priceId?.yearly;
    
    if (priceId === monthlyPriceId || priceId === yearlyPriceId) {
      return tier;
    }
  }
  
  return null;
}

/**
 * Get priceIds for a tier (both monthly and yearly)
 * @param {string} tierId - Tier ID
 * @returns {Object|null} Object with monthly and yearly priceIds, or null
 */
export function getPriceIdsForTier(tierId) {
  const tier = getTier(tierId);
  if (!tier) return null;
  
  return {
    monthly: tier.stripe?.priceId?.monthly || null,
    yearly: tier.stripe?.priceId?.yearly || null,
  };
}

/**
 * Check if a tier has access to a feature
 * @param {string|Object} tier - Tier ID or tier object
 * @param {string} featureName - Feature name (e.g., 'ai_copilot')
 * @returns {boolean} True if tier has access to feature
 */
export function hasFeature(tier, featureName) {
  const tierObj = typeof tier === 'string' ? getTier(tier) : tier;
  if (!tierObj || !tierObj.features) return false;
  
  return tierObj.features[featureName] === true;
}

/**
 * Check if a tier has access to content type
 * @param {string|Object} tier - Tier ID or tier object
 * @param {string} contentType - Content type (e.g., 'personal_credit', 'business_credit')
 * @returns {boolean} True if tier has access to all content of this type
 */
export function hasContentAccess(tier, contentType) {
  const tierObj = typeof tier === 'string' ? getTier(tier) : tier;
  if (!tierObj || !tierObj.content || !tierObj.content.articles) return false;
  
  const access = tierObj.content.articles[contentType];
  return access === 'all';
}

/**
 * Check if a tier has access to inventory type
 * @param {string|Object} tier - Tier ID or tier object
 * @param {string} inventoryType - Inventory type (e.g., 'personal_credit_cards', 'business_credit_cards')
 * @returns {boolean} True if tier has access to this inventory type
 */
export function hasInventoryAccess(tier, inventoryType) {
  const tierObj = typeof tier === 'string' ? getTier(tier) : tier;
  if (!tierObj || !tierObj.inventory_access) return false;
  
  return tierObj.inventory_access[inventoryType] === true;
}

/**
 * Get limit for a tier
 * @param {string|Object} tier - Tier ID or tier object
 * @param {string} limitType - Limit type (e.g., 'smart_planning_generations')
 * @returns {number|null} Limit value or null if not found
 */
export function getLimit(tier, limitType) {
  const tierObj = typeof tier === 'string' ? getTier(tier) : tier;
  if (!tierObj || !tierObj.limits || !tierObj.limits[limitType]) return null;
  
  const limit = tierObj.limits[limitType];
  return limit.monthly || limit.value || null;
}

/**
 * Compare tiers - check if tier is at least minimum tier
 * @param {string|Object} tier - Tier ID or tier object
 * @param {string} minimumTier - Minimum required tier ID
 * @returns {boolean} True if tier meets or exceeds minimum tier
 */
export function isTierAtLeast(tier, minimumTier) {
  const hierarchy = getTierHierarchy();
  const tierId = typeof tier === 'string' ? tier : (tier?.id || 'free');
  
  const tierIndex = hierarchy.indexOf(tierId);
  const minimumIndex = hierarchy.indexOf(minimumTier);
  
  if (tierIndex === -1 || minimumIndex === -1) {
    return false;
  }
  
  return tierIndex >= minimumIndex;
}

/**
 * Normalize tier ID (strip billing period suffix if present)
 * @param {string} tierId - Tier ID (e.g., 'basic_monthly' or 'basic')
 * @returns {string} Normalized tier ID (e.g., 'basic')
 */
export function normalizeTierId(tierId) {
  if (!tierId) return 'free';
  
  // Remove _monthly or _yearly suffix if present
  return tierId.replace(/_monthly$|_yearly$/, '');
}

/**
 * Get tier display name
 * @param {string|Object} tier - Tier ID or tier object
 * @returns {string} Tier display name
 */
export function getTierName(tier) {
  const tierObj = typeof tier === 'string' ? getTier(tier) : tier;
  return tierObj?.name || 'Free';
}

/**
 * Get tier description
 * @param {string|Object} tier - Tier ID or tier object
 * @returns {string} Tier description
 */
export function getTierDescription(tier) {
  const tierObj = typeof tier === 'string' ? getTier(tier) : tier;
  return tierObj?.description || '';
}

/**
 * Get tier price
 * @param {string|Object} tier - Tier ID or tier object
 * @param {string} period - 'monthly' or 'yearly'
 * @returns {number} Tier price
 */
export function getTierPrice(tier, period = 'monthly') {
  const tierObj = typeof tier === 'string' ? getTier(tier) : tier;
  if (!tierObj || !tierObj.price) return 0;
  
  return tierObj.price[period] || tierObj.price.monthly || 0;
}

export default {
  loadTierConfig,
  getTier,
  getAllTiers,
  getTierHierarchy,
  getTierFromPriceId,
  getPriceIdsForTier,
  hasFeature,
  hasContentAccess,
  hasInventoryAccess,
  getLimit,
  isTierAtLeast,
  normalizeTierId,
  getTierName,
  getTierDescription,
  getTierPrice,
};

